import { h } from 'preact';
import { useEffect } from 'preact/hooks';
import PropTypes from 'prop-types';

import { authStore } from '../../../global-store/subjects/auth';
import { useSubscribe } from '../../hooks/use-subscribe';
import { useRefresh } from '../../hooks/use-refresh';
import { addClassToNodes, removeClassFromNodes } from '../../../utils/dom-utils';
import { getSvgImgSrc } from '../../../utils/string-utils';
import { getHandleSlimBannerPersonalizationClick } from '../../utils/slim-banner';
import { modifierList as iconsModifiersList } from '../../components/1-foundation/icons/props-list';
import SlimBannerBottomComponent from '../../components/4-organisms/slim-banner-bottom/slim-banner-bottom';
import { useToggleBannerBottomPersonalization, useWithBannerBottom } from '../../hooks/use-banner-bottom';
import { isStickyCarouselTemplateEnable } from '../../../utils/aem-utils';
import { BANNER_BOTTOM_PERSONALIZATION_STATUS, setStatusOnLocalStorage } from '../../../utils/local-storage';
import { satelliteTracker } from '../../../services/tracking/satellite-tracking-service';
import {
  slimBannerBottomActions as actions,
  slimBannerBottomStore,
} from '../../../global-store/subjects/slim-banner-bottom/slim-banner-bottom';

import { doWidgetQuery } from './api';

const WITH_BANNER_CLASSNAME = 'u-with-banner-bottom';
const BANNER_MODIFIERS = ['slim'];
const videoPlayerClassname = isStickyCarouselTemplateEnable() ? '.j-player-carousel-element' : '.j-player';
const VIDEO_PLAYERS = document.querySelectorAll(videoPlayerClassname);
const WPN_FAB_POPUP = document.querySelectorAll('.j-wpn-fab-popup');

const SlimBannerBottomPersonalization = ({ widget, htmlProps }) => {
  const authState = useSubscribe(authStore);
  const state = useSubscribe(slimBannerBottomStore);
  useRefresh({ widgetName: widget.name, errorStatus: state.error?.status, errorCode: state.error?.code });

  // CUSTOM HOOKS
  useToggleBannerBottomPersonalization({ state, actions, htmlProps });
  useWithBannerBottom({ state });

  // UTILS/HANDLERS
  const handleClick = (event) => {
    satelliteTracker.trackInWeb('slimBannerBottom_personalizza');
    getHandleSlimBannerPersonalizationClick({
      isUserLogged: authState.isUserLogged,
      widgetId: htmlProps.widgetId,
      widgetTab: htmlProps.widgetTab,
      bannerBottom: {
        key: BANNER_BOTTOM_PERSONALIZATION_STATUS.OBJECT_KEY,
        name: BANNER_BOTTOM_PERSONALIZATION_STATUS.OK.NAME,
        expirationDays: BANNER_BOTTOM_PERSONALIZATION_STATUS.OK.EXPIRATION_DAYS,
      },
    })(event);
  };

  const handleClose = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setStatusOnLocalStorage({
      key: BANNER_BOTTOM_PERSONALIZATION_STATUS.OBJECT_KEY,
      name: BANNER_BOTTOM_PERSONALIZATION_STATUS.KO.NAME,
      expirationDays: BANNER_BOTTOM_PERSONALIZATION_STATUS.KO.EXPIRATION_DAYS,
    });
    actions.hideBannerBottom();
  };

  const renderMarketingText = () => (authState.isUserLogged ? htmlProps.postLoginText : htmlProps.preLoginText);

  useEffect(() => {
    // First query or after token refresh
    if (authState.userToken) {
      doWidgetQuery({ widget, widgetId: htmlProps.widgetAppsyncId, actions });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.userToken]);

  useEffect(() => {
    if (state.showBanner) {
      addClassToNodes({ nodes: VIDEO_PLAYERS, className: WITH_BANNER_CLASSNAME });
      addClassToNodes({ nodes: WPN_FAB_POPUP, className: WITH_BANNER_CLASSNAME });
    } else {
      removeClassFromNodes(VIDEO_PLAYERS, WITH_BANNER_CLASSNAME);
      removeClassFromNodes(WPN_FAB_POPUP, WITH_BANNER_CLASSNAME);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.showBanner]);

  if (state.showBanner === undefined) {
    return null;
  }

  if (state.showBanner) {
    const sectionDividerModifiers = state.showBanner ? ['fixed-bottom'] : ['fixed-bottom', 'slide-out'];
    return (
      <SlimBannerBottomComponent
        modifiers={BANNER_MODIFIERS}
        target={htmlProps.target}
        isWrapping={!authState.isLoginLoading}
        onClick={handleClick}
        onClose={handleClose}
        sectionDividerModifiers={sectionDividerModifiers}
        imgSrc={getSvgImgSrc(htmlProps.standardLogo)}
        text={renderMarketingText()}
        iconsColor={widget.styles.iconsColor}
      />
    );
  }
};

SlimBannerBottomPersonalization.propTypes = {
  htmlProps: PropTypes.shape({
    widgetId: PropTypes.string.isRequired,
    widgetAppsyncId: PropTypes.string.isRequired,
    widgetTab: PropTypes.string.isRequired,
    preLoginText: PropTypes.string.isRequired,
    postLoginText: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
  }),
  widget: PropTypes.shape({
    name: PropTypes.string.isRequired,
    htmlId: PropTypes.string.isRequired,
    services: PropTypes.shape({
      query: PropTypes.string.isRequired,
    }).isRequired,
    styles: PropTypes.shape({
      iconsColor: PropTypes.oneOf(iconsModifiersList).isRequired,
    }).isRequired,
  }).isRequired,
  $root: PropTypes.object.isRequired,
};

export default SlimBannerBottomPersonalization;
